import { useState } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import * as User from "providers/user";
import NestedMenuItems from "./NestedMenuItems";
import { useTranslate } from "react-admin";

interface nestedMenuItems {
  text: string;
  activeOnUrl: string;
  restrictedRole: string[];
  activeHelper?: string;
  cyId?: string;
}

interface NestedMenuItemProps {
  isDrawerOpen: boolean;
  nestedMenuData: nestedMenuItems[];
  title: string;
  icon: JSX.Element;
  role: string | undefined;
  closeDrawer: () => void;
  isSmall: boolean;
  cyId?: string;
  isIpadSizeScreen: boolean;
}

const NestedMenuItem: React.FC<NestedMenuItemProps> = ({
  isDrawerOpen,
  nestedMenuData,
  title,
  icon,
  role,
  closeDrawer,
  isSmall,
  cyId = "",
  isIpadSizeScreen
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { permissions } = User.useUser();
  const translate = useTranslate();

  const handleClosePopover = (): void => {
    setAnchorEl(null);
  };

  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen ? "nested-menu-item-popover" : undefined;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (isDrawerOpen) {
      setOpen(!open);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const splitPathname = window.location.pathname.split("/")[2]?.toLowerCase();

  const isNestedActive =
    nestedMenuData?.find(
      (i) =>
        i?.activeHelper && window.location.pathname.includes(i?.activeHelper)
    ) ||
    nestedMenuData?.find(
      (i) =>
        !i?.activeHelper &&
        permissions?.companyId &&
        !window.location.pathname.includes(permissions?.companyId) &&
        i.activeOnUrl.split("/")[2]?.toLowerCase() === splitPathname
    );

  const menuItemsStyles = {
    minWidth: "47px",
    backgroundColor:
      (!isDrawerOpen && isNestedActive) || (!open && isNestedActive)
        ? "rgba(184, 114, 42, 0.08)"
        : "#FAF9F7",
    margin: isDrawerOpen ? "" : "0px 8px",
    borderRadius: open && isDrawerOpen ? "24px 24px 0px 0px" : "24px",
    paddingX: isDrawerOpen ? "" : "12px",
    gap: "-4px",
    paddingY: "6px",
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: isIpadSizeScreen ? "14px" : "16px",
    color: "rgba(48, 47, 46, 0.95)",
    marginBottom: isDrawerOpen ? "" : "4px",
    "&:hover": {
      backgroundColor:
        (!isDrawerOpen && isNestedActive) || (!open && isNestedActive)
          ? "rgba(184, 114, 42, 0.08)"
          : "rgba(0, 0, 0, 0.04)"
    },
    "& svg": {
      fill:
        (!isDrawerOpen && isNestedActive) || (!open && isNestedActive)
          ? "#B8722A"
          : "",
      "& g": {
        "& path": {
          fill:
            (!isDrawerOpen && isNestedActive) || (!open && isNestedActive)
              ? "#B8722A"
              : "#302F2E",
          fillOpacity:
            (!isDrawerOpen && isNestedActive) || (!open && isNestedActive)
              ? "1"
              : "0.56"
        }
      }
    }
  };

  const arrowIconStyles = {
    width: "24px",
    height: "24px",
    fill: "rgba(48, 47, 46, 0.56)"
  };
  return (
    <div
      className={`${
        open && isDrawerOpen
          ? "mx-[8px] border rounded-[24px] mb-[4px] border-[#B8722A1F]"
          : !open && isDrawerOpen
          ? "mx-[8px] border rounded-[24px] mb-[4px] border-[#FAF9F7]"
          : ""
      }`}
    >
      <Tooltip
        title={!isDrawerOpen ? translate(`navigation.${title}`) : ""}
        placement="right"
      >
        <ListItemButton
          onClick={handleClick}
          data-cy={cyId}
          sx={
            !isDrawerOpen && isNestedActive
              ? {
                  ...menuItemsStyles,
                  backgroundColor: "rgba(184, 114, 42, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(184, 114, 42, 0.08)"
                  }
                }
              : menuItemsStyles
          }
        >
          <ListItemIcon sx={{ minWidth: isIpadSizeScreen ? "44px" : "52px" }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={
              <span
                style={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: isIpadSizeScreen ? "14px" : "16px",
                  color: "rgba(48, 47, 46, 0.95)"
                }}
              >
                {!isDrawerOpen ? title : translate(`navigation.${title}`)}
              </span>
            }
          />
          {open ? (
            <ExpandLess style={arrowIconStyles} />
          ) : (
            <ExpandMore style={arrowIconStyles} />
          )}
        </ListItemButton>
      </Tooltip>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" className="rounded-[32px]" disablePadding>
          {role &&
            isDrawerOpen &&
            !!nestedMenuData?.length &&
            nestedMenuData
              ?.filter((i) => !i.restrictedRole.includes(role))
              ?.filter((i) =>
                permissions?.isCompanyLimited
                  ? !i.restrictedRole.includes("LIMITED_ACCESS")
                  : true
              )
              ?.map((item) => {
                const isActive =
                  (item?.activeHelper &&
                    window.location.pathname.includes(item?.activeHelper)) ||
                  !!(
                    !item?.activeHelper &&
                    permissions?.companyId &&
                    !window.location.pathname.includes(
                      permissions?.companyId
                    ) &&
                    splitPathname ===
                      item.activeOnUrl.split("/")[2]?.toLowerCase()
                  );
                return (
                  <NestedMenuItems
                    key={item.text}
                    closeDrawer={closeDrawer}
                    isSmall={isSmall}
                    isDrawerOpen={isDrawerOpen}
                    cyId={item.cyId}
                    item={item}
                    isNestedActive={!!isNestedActive}
                    open={open}
                    isActive={isActive}
                    isIpadSizeScreen={isIpadSizeScreen}
                  />
                );
              })}
        </List>
      </Collapse>
      <Popover
        id={id}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        elevation={4}
        sx={{
          "& .MuiPopover-paper": {
            backgroundColor: "#fff",
            borderRadius: "24px"
          }
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: 64
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        <List component="div" className="rounded-[32px]" disablePadding>
          {role &&
            !!nestedMenuData?.length &&
            nestedMenuData
              ?.filter((i) => !i.restrictedRole.includes(role))
              ?.filter((i) =>
                permissions?.isCompanyLimited
                  ? !i.restrictedRole.includes("LIMITED_ACCESS")
                  : true
              )
              ?.map((item) => {
                const isActive =
                  (item?.activeHelper &&
                    window.location.pathname.includes(item?.activeHelper)) ||
                  !!(
                    !item?.activeHelper &&
                    permissions?.companyId &&
                    !window.location.pathname.includes(
                      permissions?.companyId
                    ) &&
                    splitPathname ===
                      item.activeOnUrl.split("/")[2]?.toLowerCase()
                  );
                return (
                  <NestedMenuItems
                    key={item.text}
                    closeDrawer={closeDrawer}
                    isSmall={isSmall}
                    isDrawerOpen={isDrawerOpen}
                    item={item}
                    cyId={item.cyId}
                    isNestedActive={!!isNestedActive}
                    open={open}
                    isActive={isActive}
                    fromPopover
                    handleClosePopover={handleClosePopover}
                    isIpadSizeScreen={isIpadSizeScreen}
                  />
                );
              })}
        </List>
      </Popover>
    </div>
  );
};

export default NestedMenuItem;
